import React, { useState } from "react";

// UI components
import { Tooltip } from "@mui/material";
import { ChevronDown, ChevronRight, Copy, Check } from "react-feather";

// Every value on this screen is shown with the API path it came from, so the
// path chip is a primitive rather than a decoration.
export function ApiPath({ path, title }) {
  const [copied, setCopied] = useState(false);

  if (!path) return null;

  const copy = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(path).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  return (
    <Tooltip title={title || "Copy path"} placement="top" arrow>
      <button type="button" className="rhp-path" onClick={copy}>
        <code>{path}</code>
        {copied ? <Check size={11} /> : <Copy size={11} />}
      </button>
    </Tooltip>
  );
}

export function Field({ label, value, path, suffix, empty, children }) {
  const hasValue =
    children != null ||
    (value != null && value !== "" && !(Array.isArray(value) && !value.length));

  return (
    <div className={`rhp-field${hasValue ? "" : " is-empty"}`}>
      <div className="rhp-field__label">{label}</div>
      <div className="rhp-field__value">
        {hasValue ? (
          children || (
            <>
              {value}
              {suffix ? <span className="rhp-field__suffix">{suffix}</span> : null}
            </>
          )
        ) : (
          <span className="rhp-field__empty">{empty || "Not published"}</span>
        )}
      </div>
      <ApiPath path={path} />
    </div>
  );
}

// Taxonomy entries come back stripped often enough (`{}`, a bare `[]`,
// `{text: "3"}` with no feature) that a label is never assumed: anything
// without one is dropped rather than rendered as an object.
const tagLabel = (item) => {
  if (typeof item === "string" || typeof item === "number") return String(item);
  const label = item && (item.label || item.name || item.description);
  return typeof label === "string" ? label : "";
};

const tagBadge = (item) => {
  const badge = item && (item.number ?? item.text);
  return badge == null || badge === "" ? "" : String(badge);
};

export function TagList({ items, path, empty }) {
  const tags = (items || [])
    .map((item) => ({ label: tagLabel(item), badge: tagBadge(item) }))
    .filter((tag) => tag.label);

  if (!tags.length) {
    return (
      <div className="rhp-taglist is-empty">
        <span className="rhp-field__empty">{empty || "Not published"}</span>
        <ApiPath path={path} />
      </div>
    );
  }

  return (
    <div className="rhp-taglist">
      <div className="rhp-tags">
        {tags.map((tag, i) => (
          <span className="rhp-tag" key={`${tag.label}-${i}`}>
            {tag.label}
            {tag.badge ? <b>{tag.badge}</b> : null}
          </span>
        ))}
      </div>
      <ApiPath path={path} />
    </div>
  );
}

export function Section({ id, title, subtitle, path, raw, children }) {
  const [showRaw, setShowRaw] = useState(false);

  return (
    <section className="rhp-section" id={id}>
      <header className="rhp-section__head">
        <div>
          <h2 className="rhp-section__title">{title}</h2>
          {subtitle ? (
            <p className="rhp-section__subtitle">{subtitle}</p>
          ) : null}
          <ApiPath path={path} />
        </div>
        {raw !== undefined ? (
          <button
            type="button"
            className="rhp-raw-toggle"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Raw JSON
          </button>
        ) : null}
      </header>

      {showRaw ? (
        <pre className="rhp-raw">{JSON.stringify(raw ?? null, null, 2)}</pre>
      ) : null}

      <div className="rhp-section__body">{children}</div>
    </section>
  );
}

export function Notice({ tone = "info", children }) {
  return <div className={`rhp-notice rhp-notice--${tone}`}>{children}</div>;
}
